import React, { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useTranslation } from 'react-i18next';
import { Table } from 'semantic-ui-react';
import { InfiniteScrollPageLoader } from 'nallian-shared-ui/lib/modules';
import HasSomeRights from 'nallian-shared-ui/lib/layout/rights/HasSomeRights';
import { history } from 'nallian-shared-ui/lib/config/history';
import { Modal, Dimmer, Loader, Divider, Flex } from 'nallian-shared-ui/lib/components';
import userGroupMembersService from '../../services/userGroups/userGroupMembersService';
import userGroupService from '../../services/userGroups/userGroupService';
import removeUserGroupMemberService from '../../services/userGroups/removeUserGroupMemberService';
import createUserGroupMemberService from '../../services/userGroups/createUserGroupMemberService';
import AddUserGroupMember from './AddUserGroupMember';
import UserGroupMember from './UserGroupMember';

const USER_GROUP_ASSIGN_MEMBER_RIGHTS = [
  'user-groups/assign/own-organisation',
  'user-groups/assign/system-organisation',
  'user-groups/assign/non-system-organisation',
];

const UserGroupMembers = ({
  open,
  dispatchUserGroupMembersGet,
  userGroupMembersFetch,
  dispatchRemoveUserGroupMemberDelete,
  dispatchCreateUserGroupMemberPost,
  dispatchUserGroupMembersPageGet,
  userGroupMembersPageFetch,
  dispatchUserGroupGet,
  userGroupFetch,
  organisationId,
  urlRoot,
}) => {
  const { t } = useTranslation();
  const { actionId } = useParams();

  useEffect(() => {
    if (open) dispatchUserGroupMembersGet(organisationId, actionId, t);
  }, [open, organisationId, actionId, dispatchUserGroupMembersGet, t]);

  useEffect(() => {
    if (open) dispatchUserGroupGet(organisationId, actionId, t);
  }, [open, organisationId, actionId, dispatchUserGroupGet, t]);

  const handleRemoveMember = useCallback(
    id => dispatchRemoveUserGroupMemberDelete(organisationId, actionId, id, t),
    [dispatchRemoveUserGroupMemberDelete, actionId, organisationId, t]
  );

  const handleAddMember = useCallback(
    principalId => {
      if (principalId)
        dispatchCreateUserGroupMemberPost(organisationId, actionId, { principalId }, t);
    },
    [dispatchCreateUserGroupMemberPost, actionId, organisationId, t]
  );

  const handleDispatchPage = useCallback(
    page => dispatchUserGroupMembersPageGet(organisationId, actionId, page),
    [dispatchUserGroupMembersPageGet, organisationId, actionId]
  );

  const handleModalClose = useCallback(() => history.push(`${urlRoot}/user-groups`), [urlRoot]);

  useEffect(() => document.querySelector('.dimmer ')?.setAttribute('id', 'userMembersParent'));

  if (!open) return null;

  return (
    <Modal open closeIcon onClose={handleModalClose}>
      <Dimmer active={userGroupMembersFetch.pending} inverted>
        <Loader />
      </Dimmer>
      <Modal.Header>
        <Flex alignItems="center">
          {t('membersOfUserGroup')}&nbsp;-&nbsp;
          {userGroupFetch.value?.name}
        </Flex>
      </Modal.Header>
      <Modal.Content>
        <HasSomeRights rights={USER_GROUP_ASSIGN_MEMBER_RIGHTS}>
          <>
            <AddUserGroupMember
              organisationId={organisationId}
              members={userGroupMembersFetch.value}
              handleAddMember={handleAddMember}
            />
            <Divider />
          </>
        </HasSomeRights>
        {!userGroupMembersFetch.pending && userGroupMembersFetch.value?.length <= 0 ? (
          <Flex>{t('userGroupHasNoMembers')}</Flex>
        ) : (
          <>
            <Table basic="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width="12">{t('name')}</Table.HeaderCell>
                  <Table.HeaderCell width="4" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {((!userGroupMembersFetch.pending && userGroupMembersFetch.value) || []).map(
                  member => (
                    <UserGroupMember
                      key={member.id}
                      member={member}
                      handleRemoveMember={handleRemoveMember}
                    />
                  )
                )}
              </Table.Body>
            </Table>
          </>
        )}
        {userGroupMembersFetch.fulfilled && (
          <InfiniteScrollPageLoader
            list={userGroupMembersFetch.value}
            pageList={userGroupMembersPageFetch.value}
            onTrigger={handleDispatchPage}
            loading={!!userGroupMembersPageFetch.pending}
            pageSize={100}
            customScrollSurface="userMembersParent"
          />
        )}
      </Modal.Content>
    </Modal>
  );
};

UserGroupMembers.propTypes = {
  dispatchUserGroupMembersGet: PropTypes.func.isRequired,
  dispatchUserGroupMembersPageGet: PropTypes.func.isRequired,
  userGroupMembersFetch: PropTypes.object.isRequired,
  userGroupMembersPageFetch: PropTypes.object.isRequired,
  dispatchUserGroupGet: PropTypes.func.isRequired,
  userGroupFetch: PropTypes.object.isRequired,
  dispatchRemoveUserGroupMemberDelete: PropTypes.func.isRequired,
  removeUserGroupMemberFetch: PropTypes.object.isRequired,
  dispatchCreateUserGroupMemberPost: PropTypes.func.isRequired,
  createUserGroupMemberFetch: PropTypes.object.isRequired,
  organisationId: PropTypes.string.isRequired,
  urlRoot: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
};

export default compose(
  createUserGroupMemberService,
  removeUserGroupMemberService,
  userGroupService,
  userGroupMembersService
)(UserGroupMembers);
